import DOMPurify from "dompurify"

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this when rendering any user-provided HTML content
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "p", "br", "ul", "ol", "li", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
  })
}

/**
 * Sanitize a string by stripping all HTML tags
 * Use this for plain text inputs that should never contain HTML
 */
export function sanitizeText(dirty: string): string {
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
}

/**
 * Sanitize user input for safe display
 * Escapes HTML entities to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}

/**
 * Validate and sanitize a URL
 * Returns empty string if URL is invalid or potentially malicious
 */
export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url)
    // Only allow http, https protocols
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return ""
    }
    return parsed.toString()
  } catch {
    return ""
  }
}

/**
 * Sanitize object values recursively
 * Useful for sanitizing form data before storage
 */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): T {
  const result = { ...obj }
  for (const key in result) {
    const value = result[key]
    if (typeof value === "string") {
      ;(result as Record<string, unknown>)[key] = sanitizeText(value)
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      ;(result as Record<string, unknown>)[key] = sanitizeObject(value as Record<string, unknown>)
    }
  }
  return result
}
